import React, { useRef, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretLeft, faCaretRight } from "@fortawesome/free-solid-svg-icons";

const NextArrow = ({ onClick }) => (
  <div className="slick-custom-arrow slick-next" onClick={onClick}>
    <FontAwesomeIcon icon={faCaretRight} />
  </div>
);

const PrevArrow = ({ onClick }) => (
  <div className="slick-custom-arrow slick-prev" onClick={onClick}>
    <FontAwesomeIcon icon={faCaretLeft} />
  </div>
);

const Section7 = ({ url }) => {
  const sectionRef = useRef(null);
  const [Slick, setSlick] = useState(null);
  const slickLoaded = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      async (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !slickLoaded.current) {
          slickLoaded.current = true;
          const slickModule = await import("react-slick");
          await import("slick-carousel/slick/slick.css");
          await import("slick-carousel/slick/slick-theme.css");
          setSlick(() => slickModule.default);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const settings = {
    dots: true,
    arrows: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    pauseOnHover: true,
    speed: 600,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

const cases = [
  {
    image: `${url}images/case-studies/evGo.png`,
    title: "Offshore Team Delivering Enterprise-Grade AI Platform",
    tags: ["Energy", "Enterprise", "USA"],
    client: "EVgo, United States",
    industry:
      "EV Charging Network / Energy Infrastructure | US Enterprise (1,100+ stations across 40 states)",
    problem:
      "EVgo needed a custom AI-enabled financial intelligence platform to replace manual spreadsheet processes across 1,100+ charging sites, without the cost or timeline of building a local US engineering team.",
    built:
      "ExD Digital's offshore development team delivered a complete AI-enabled financial platform, including an automated bill validation engine, portfolio-wide financial modeling system, and CAPEX estimator, built on Python, Django, and predictive analytics with third-party billing API integration.",
    result:
      "Enterprise-grade AI financial platform delivered to US enterprise standards, fully automating electricity bill validation and financial modeling across a nationwide network, replacing spreadsheet-driven manual processes and enabling data-backed investment planning at scale.",
    quote:
      "ExD helped us replace manual, spreadsheet-driven processes with a more structured financial intelligence platform, enabling clearer insight, better forecasting, and more confident decision-making as we expand.",
    quoteAuthor: "Badar Khan, CEO @ EVgo",
  },
  {
    image: `${url}images/case-studies/embark.png`,
    title: "Offshore Team Delivering AI Sports Platform",
    tags: ["Sports Technology", "AI", "North America"],
    client: "Embark Athletics (Best Coach Ever), Canada / USA",
    industry:
      "Sports Technology / Youth Sports Coaching | Canada and USA client",
    problem:
      "Embark Athletics needed a sophisticated AI-powered coaching platform to scale youth sports programs across Canada and the US, without the cost of assembling a local development team in North America.",
    built:
      "ExD Digital's offshore team delivered a full-stack AI coaching platform including an intelligent drill generation engine, AI-personalized training plans, and dynamic game phase modeling, built on Python, Django, PostgreSQL, and OpenAI with Claude Sonnet 4.5 and Google Gemini integration.",
    result:
      "Complete delivery of a production AI sports platform, enabling coaches to generate personalized AI-driven training programs at scale and transforming traditional coaching into a data-driven, repeatable, and scalable process.",
    quote:
      "ExD worked closely with us to design and build an AI-powered coaching platform that brings structure, adaptability, and intelligence into the training process.",
    quoteAuthor: "CEO @ Embark Athletics",
  },
  {
    image: `${url}images/case-studies/movig.png`,
    title: "Offshore Full-Stack Team Building a Creator Marketplace",
    tags: ["Digital Media", "Startup", "Global"],
    client: "Movig, United States / Global",
    industry:
      "Digital Media / Creator Economy / Marketing Technology | US-based startup, global reach",
    problem:
      "Movig needed a technically complex AI-enabled marketplace platform built rapidly, without the cost and timeline of hiring a full-stack team locally in the US.",
    built:
      "ExD Digital's offshore team delivered a complete AI-enabled creator marketplace with a Python/Django backend, React.js frontend, PostgreSQL database, and full OpenAI GPT-4 and Claude AI integration, from scoping to production deployment.",
    result:
      "Production-grade AI marketplace platform enabling scalable creator-brand automation, replacing manual partnership processes and allowing high-volume interactions without proportional increases in operational overhead.",
    quote:
      "ExD helped us build a centralized platform that brought structure and automation to partnerships, enabling more consistent revenue opportunities for creators and a smoother sourcing experience for brands.",
    quoteAuthor: "CEO @ Movig",
  },
];

  return (
    <section className="home-section-7" ref={sectionRef}>
      <div className="container overflow-hidden">
        <h2 className="s7-heading" data-aos="fade-up">CASE STUDIES</h2>

        <div className="s7-slider-wrapper" data-aos="fade-up" data-aos-delay="150">
          {Slick ? (
            <Slick {...settings}>
              {cases.map((c, idx) => (
                <div key={idx}>
                  <div className="s7-card">

                    {/* LEFT — image */}
                    <div className="s7-card-image">
                      <img src={c.image} alt={c.title} />
                    </div>

                    {/* RIGHT — content */}
                    <div className="s7-card-content">
                      <h3 className="s7-card-title">{c.title}</h3>

                      <div className="s7-tags">
                        {c.tags.map((tag, i) => (
                          <React.Fragment key={i}>
                            <span className="s7-tag">{tag}</span>
                            {i < c.tags.length - 1 && <span className="s7-tag-sep">|</span>}
                          </React.Fragment>
                        ))}
                      </div>

                      <p className="s7-meta"><strong>Client:</strong> {c.client}</p>
                      <p className="s7-meta"><strong>Industry & Size:</strong> {c.industry}</p>
                      <p className="s7-meta"><strong>The Problem:</strong> {c.problem}</p>
                      <p className="s7-meta"><strong>What We Built:</strong> {c.built}</p>
                      <p className="s7-meta"><strong>The Result:</strong> {c.result}</p>

                      <div className="s7-quote-box">
                        <div className="s7-quote-avatar" />
                        <div>
                          <p className="s7-quote-text">"{c.quote}"</p>
                          <p className="s7-quote-author">{c.quoteAuthor}</p>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              ))}
            </Slick>
          ) : (
            <div className="s7-skeleton" />
          )}
        </div>
      </div>
    </section>
  );
};

export default Section7;